'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { hospitalConfig } from '@/data/hospitalConfig';
import { trackEvent } from '@/lib/analytics';

export default function WelcomeHero() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <main
      className="gradient-bg min-h-screen flex flex-col relative overflow-hidden"
      role="main"
      aria-label="All Is Well Experience — Welcome"
    >
      {/* Decorative background orbs */}
      <div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #C9A84C, transparent 70%)' }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-1/3 right-1/4 w-64 h-64 rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #6B21D4, transparent 70%)' }}
        aria-hidden="true"
      />
      {/* Top nav */}
      <nav
        className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5"
        role="navigation"
        aria-label="Top navigation"
      >
        <div className="logo-pill">
          <AppImage
            src={hospitalConfig?.logo}
            alt="All Is Well Multi-Speciality Hospital Logo"
            width={120}
            height={48}
            className="h-10 w-auto object-contain"
            priority
          />
        </div>
        <a
          href={hospitalConfig?.emergencyTel}
          className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors text-xs font-bold tracking-widest uppercase"
          aria-label="Emergency — Call All Is Well Hospital"
          onClick={() => trackEvent({ event: 'emergency_clicked', source: 'welcome-nav' })}
        >
          <span className="animate-pulse-gold">🚨</span>
          <span className="hidden sm:inline">Emergency</span>
        </a>
      </nav>
      {/* Hero content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 md:px-12 text-center relative z-10 pb-24 md:pb-16">
        <div
          className={`transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Eyebrow */}
          <p className="section-label mb-6 tracking-widest">
            {hospitalConfig?.experienceName}
          </p>

          {/* Main headline */}
          <h1 className="text-display-xl text-white mb-4 font-bold leading-tight max-w-3xl mx-auto">
            WELCOME TO<br />
            <span className="gold-gradient-text">ALL IS WELL</span><br />
            HOSPITAL
          </h1>

          {/* Tagline */}
          <p className="text-sm md:text-base font-semibold tracking-[0.2em] text-primary uppercase mb-2">
            {hospitalConfig?.tagline}
          </p>
          <p className="text-muted-foreground text-base italic mb-10">
            {hospitalConfig?.brandTagline}
          </p>

          {/* Primary CTA */}
          <div className="flex flex-col items-center gap-4 mb-8">
            <Link
              href="/digital-reception-screen"
              onClick={() => trackEvent({ event: 'journey_started', source: 'welcome-hero' })}
              className="btn-primary text-sm md:text-base px-10 py-4 tracking-widest w-full max-w-xs"
              aria-label="Begin your care journey with All Is Well Hospital"
            >
              BEGIN MY JOURNEY →
            </Link>
          </div>

          {/* Secondary CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap">
            <Link
              href="/find-doctor"
              className="btn-ghost-white text-xs px-6 py-3 tracking-widest"
              aria-label="Find a doctor at All Is Well Hospital"
            >
              FIND A DOCTOR
            </Link>
            <Link
              href="/appointment-booking-screen"
              className="btn-ghost-white text-xs px-6 py-3 tracking-widest"
              aria-label="Book an appointment at All Is Well Hospital"
            >
              BOOK APPOINTMENT
            </Link>
            <Link
              href="/emergency"
              className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors text-xs font-bold tracking-widest uppercase border border-red-800 hover:border-red-600 rounded-lg px-6 py-3 min-h-[48px]"
              aria-label="Go to emergency page"
              onClick={() => trackEvent({ event: 'emergency_clicked', source: 'welcome-secondary' })}
            >
              EMERGENCY
            </Link>
          </div>
        </div>
      </div>
      {/* Bottom address bar */}
      <div className="relative z-10 border-t border-border px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground text-center sm:text-left">
          📍 {hospitalConfig?.address}
        </p>
        <div className="flex items-center gap-4">
          <p className="text-xs text-muted-foreground">{hospitalConfig?.hours?.weekdays}</p>
          <p className="text-xs text-muted-foreground hidden sm:block">{hospitalConfig?.hours?.weekends}</p>
        </div>
      </div>
    </main>
  );
}